import { WAKEUP_FORM_DATA } from 'common/common.constants';
import { axiosCustom, getFormatDate, getJobResultExcelStyle, getOtherExcelStyle } from 'common/common.function';
import { WakeUp } from 'common/interface/props';
import { ISearchData } from 'components/admin/interfaces/admin.interface';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import XLSX from 'xlsx';
import DatePicker, { registerLocale } from 'react-datepicker';
import ko from 'date-fns/locale/ko';
import styled, { ThemeProvider } from 'styled-components';
import { useMutation } from 'react-query';
import { detailJobExcelMutation, jobDetailSearchExcelMutation } from 'components/form/api/admin.api';

// atomic component
import Container from 'components/UI/atoms/Container';
import Text from 'components/UI/atoms/Text';
import Grid from 'components/UI/atoms/Grid';
import Button from 'components/UI/atoms/Button';
import TextField from 'components/UI/atoms/TextField';
import Table from 'components/UI/atoms/Table';
import Select from 'components/UI/atoms/Select';
import Pagination from 'components/UI/molecules/Pagination';
import { BiSearch } from 'react-icons/bi';
import { RiFileExcel2Line, RiAlarmWarningFill } from 'react-icons/ri';
import SendRedmine from 'components/form/SendRedmine';
import { SiRedmine } from 'react-icons/si';
import { IPostMessage } from '.';

// 이 줄은 accessibility를 위한 필수 설정입니다.
const MySwal = withReactContent(Swal);
registerLocale('ko', ko);

interface IDashboard {
  solType: WakeUp;
  isIntegrated?: boolean;
  searchData: ISearchData;
  setSearchData: React.Dispatch<React.SetStateAction<ISearchData>>;
}

interface IJobData {
  startDate: string;
  allEndDate: string;
  scrapShopCode: string;
  scrapShopName: string;
  targetShopName: string;
  scrapShopId: string;
  state: string;
  scrapType: string;
  msg: string;
  jobId: number;
  email: string;
  wakeupId: string;
  wakeupEtc1: string;
  wakeupEtc2: string;
  wakeupEtc5: string;
  successCount?: number;
  failureCount?: number;
}

interface ISendRedmineData {
  /**
   * @alias 작업 아이디
   */
  jobId: number;
  /**
   * @alias 문의 내용
   */
  count: number;
  /**
   * @alias 웨이크업 이메일
   */
  email: string;
  /**
   * @alias 가져오는 쇼핑몰 이름
   */
  scrapShopName: string;
  /**
   * @alias 이동하는 쇼핑몰 이름
   */
  targetShopName: string;
  /**
   * @alias 실패 메세지
   */
  failureMessage?: string;
  /**
   * @alias 작업 상태
   */
  state: string;
  /**
   * @alias 성공 카운트
   */
  successCount: number;
  /**
   * @alias 실패 카운트
   */
  failureCount: number;
}

interface IGetJobData {
  /**
   * @alias 조회 키
   */
  searchType: string;
  /**
   * @alias 조회 값
   */
  searchValue: string;
  /**
   * @alias 조회 솔루션 타입
   */
  searchSolType: WakeUp;
  /**
   * @alias 페이지 번호
   */
  nowPage: number;
}

const ButtonTag = styled(Button)`
  border: 1px solid #e2e2e2;
  padding: 5px 5px;

  display: inline-flex;
  justify-content: center;
  align-items: center;
  :hover {
    background: #f5f5f5;
  }
`;

export const StyledExcelButton = styled(ButtonTag)`
  display: inline-flex;
  align-items: center;
  margin-left: 5px;
  gap: 2px;
  padding: 4px 6px;
  font-size: 0.85rem;
  color: #000;
  width: auto;
  min-width: unset;
  &:hover {
    background: #f5f5f5;
  }
`;

export default function Dashboard({ solType, isIntegrated, searchData, setSearchData }: IDashboard) {
  // 시작일 (기본값 : 현재일 - 7일) -> 2024-06-27 시작은 7일이였으나 알수없는 사유로 60일로 변경되있음, 14일로 바꿈
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d;
  });

  // 종료일 (기본값 : 현재일)
  const [endDate, setEndDate] = useState(new Date());

  // 페이지당 노출 수
  const [viewCount, setViewCount] = useState(30);

  // 조회 솔루션 타입
  const [searchSolType, setSearchSolType] = useState(solType);
  // 조회 키
  const [searchType, setSearchType] = useState('playautoId');
  // 조회 값
  const [searchValue, setSearchValue] = useState('');

  // 현재 페이지
  const [nowPage, setNowPage] = useState(1);

  // 작업 총 수
  const [max, setMax] = useState(0);

  // 작업 데이터
  const [jobData, setJobData] = useState<IJobData[]>([]);

  // 페이지변경됐는지 확인 훅
  const [pageChanged, setPageChanged] = useState<boolean>(true);

  // 상위 컴포넌트에서 serarchData 변경시, 검색 실행
  useEffect(() => {
    if (searchData.jobId) {
      setPageChanged(true); // 페이지 변경여부

      setSearchType('jobId');
      setSearchValue(searchData.jobId);
      setSearchSolType(searchData.solType);
      setNowPage(1);

      getJobData({
        searchType: 'jobId',
        searchValue: searchData.jobId,
        searchSolType: searchData.solType,
        nowPage: 1,
      });
    }
  }, [searchData]);

  // 페이지 변경 이벤트
  useEffect(() => {
    if (pageChanged) {
      setPageChanged(false);
    } else {
      getJobData();
    }
  }, [nowPage]);

  // IP 검사
  useEffect(() => {
    async function ip() {
      try {
        await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/user/${solType.toLowerCase()}/ipCheck`, {
          headers: {
            'sol-type': isIntegrated ? 'ADMIN' : solType.toUpperCase(),
          },
        });
      } catch (error: any) {
        await Swal.fire({
          title: '실패',
          html: error.message,
          icon: 'error',
        });

        window.location.href = 'https://plto.com';
      }
    }
    ip();

    return () => {
      // 해당 처리 안해주면, 탭 이동되고 다시 돌아올때마다 이전 조회 정보를 계속 가지고 있어서 초기화 해줌
      setSearchData({
        jobId: '',
        solType: 'COUPANG',
      });
    };
  }, []);

  function onSubmit() {
    getJobData();
  }

  // 검색 이벤트
  async function getJobData(search?: IGetJobData) {
    Swal.fire({
      title: '잠시만 기다려 주세요.',
      html: '작업 조회중 ...',
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { result } = await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-search`, {
        params: {
          startDate: getFormatDate(startDate),
          endDate: getFormatDate(endDate) + ' 23:59:59',
          solType: search?.searchSolType || searchSolType,
          searchType: search?.searchType || searchType,
          searchValue: search?.searchValue || searchValue,
          viewCount: viewCount,
          nowPage: search?.nowPage || nowPage,
        },
        headers: {
          'sol-type': isIntegrated ? 'ADMIN' : solType.toUpperCase(),
        },
      });
      if (result.jobData.length <= 0 || result.totalCount === 0) {
        Swal.fire({
          title: '실패',
          html: `정보가 조회되지 않습니다.`,
          icon: 'error',
        });
      } else {
        // 작업 데이터 설정
        setJobData(result.jobData);

        // 최대 페이지 설정
        setMax(Math.ceil(result.totalCount / viewCount));

        Swal.close();
      }
    } catch (error: any) {
      await Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    }
  }

  // 작업 상세 보기/엑셀 다운로드
  async function jobDetailEvent(type: 'EXCEL' | 'VIEW', jobId: number, count: number) {
    if (type === 'VIEW' && count > 100) {
      Swal.fire({
        title: '경고',
        html: '100 개 이상 조회는 엑셀 다운로드를 이용 부탁 드립니다',
        icon: 'error',
      });
      return false;
    }

    Swal.fire({
      title: '잠시만 기다려 주세요.',
      html: '작업 상세 다운로드 ...', // add html attribute if you want or remove
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { result: jobDetailData } = await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-job/${jobId}`, {
        timeout: 300000,
        headers: {
          'sol-type': isIntegrated ? 'ADMIN' : solType.toUpperCase(),
        },
      });

      if (type === 'VIEW') {
        MySwal.fire({
          width: 1200,
          title: '작업 상세 정보',
          html: (
            <>
              <Table
                headerItems={Object.keys(jobDetailData[0]).map((one, index) => ({
                  width:
                    index === 0
                      ? 110
                      : index === 1
                      ? 130
                      : index === 2
                      ? 160
                      : index === 3
                      ? 160
                      : index === 4
                      ? 220
                      : index === 5
                      ? 200
                      : index === 6
                      ? 220
                      : index === 7
                      ? 180
                      : index === 8
                      ? 230
                      : index === 9
                      ? 160
                      : index === 10
                      ? 220
                      : 100,
                  align: 'center',
                  headerAlign: 'center',
                  headerName: one,
                  editable: true,
                }))}
                bodyItems={jobDetailData.map((prodOne: any) => Object.keys(prodOne).map((one) => prodOne[one]))}
              />
            </>
          ),
          confirmButtonText: '확인',
        });
      } else if (type === 'EXCEL') {
        const dataWS = XLSX.utils.json_to_sheet(jobDetailData);
        // 엑셀 컬럼 너비 정하기
        dataWS['!cols'] = getJobResultExcelStyle(searchSolType);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, dataWS, 'jobDetailData');
        XLSX.writeFile(wb, `${jobId}.xlsx`);
        Swal.close();
      }
    } catch (error: any) {
      await Swal.fire({
        title: '실패',
        html: `${error.message}`,
        confirmButtonText: '확인',
      });
    }
  }

  /**
   * @alias 어드민 > 작업 강제 실패처리
   * @description 작업 시작이 12시간 지났고 state가 inactive이거나 active인 작업 실패처리 하도록 함
   * 12시간에 대한 유효성 검사는 프론트에서 하고 state에 관한 유효성 검사는 백엔드에서 처리
   */
  async function jobForceFailure(jobId: number, createdAt: string) {
    /* 작업 시작 시간 */
    const jobStartDate = new Date(createdAt);
    /* 현재 시간 */
    const currentDate = new Date();
    /* 12시간을 밀리초로 계산 */
    const hours12 = 12 * 60 * 60 * 1000;
    /* 작업 시작 후 12시간이 지난 경우만 실패처리 가능 */
    if (jobStartDate !== null && currentDate.getTime() - jobStartDate.getTime() >= hours12) {
      const agreeSwal = await Swal.fire({
        title: '작업 실패',
        html: '해당 작업을 강제로 실패처리 하시겠습니까?',
        showCancelButton: true,
        cancelButtonText: '취소',
        confirmButtonText: '확인',
      });
      // 동의하지 않았을 때
      if (!agreeSwal.isConfirmed) {
        return false;
      }
      const userInfoString = localStorage.getItem('userInfo');
      let userInfo;

      try {
        userInfo = JSON.parse(userInfoString!); // 문자열을 객체로 변환
        const result = await axiosCustom.post(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/failure-job/${jobId}`, userInfo, {
          timeout: 300000,
          headers: {
            'sol-type': isIntegrated ? 'ADMIN' : solType.toUpperCase(),
          },
        });

        if (result.success == true) {
          await Swal.fire({
            title: '성공',
            html: `작업 실패 처리 되었습니다.`,
            icon: 'success',
          });
          getJobData();
        } else {
          await Swal.fire({
            title: '실패',
            html: `${result.message}`,
            icon: 'error',
          });
        }
      } catch (error: any) {
        await Swal.fire({
          title: '실패',
          html: `${error.message}`,
          icon: 'error',
        });
      }
    } else {
      await Swal.fire({
        title: '실패',
        html: '해당 작업은 12시간 이내에 시작된 작업입니다. 작업 실패 처리는 개발팀에게 문의해주세요.',
        icon: 'error',
      });
      return false;
    }
  }

  /**
   * @alias 어드민 - 레드마인 문의 남기기
   * @description 웨이크업에서 직접 레드마인 문의를 생성
   * 웨이크업 프론트 -> 백엔드 -> 레드마인 문의
   */
  async function createJobInquiry(data: ISendRedmineData) {
    try {
      // 레드마인 일감 작성 전에 토큰 유효성 검사
      const token = localStorage.getItem('jwt');
      const sendUrlResponse = await axiosCustom.get(`${process.env.REACT_APP_PAWN_ADMIN_BACKEND_ENDPOINT}/api/auth/get-user-info`, {
        headers: {
          jwt: token,
        },
      });

      // 401 토큰 만료 에러
      if (!sendUrlResponse.success && sendUrlResponse.error?.statusCode) {
        const message: IPostMessage = {
          code: 401,
          message: 'token',
        };
        window.parent.postMessage(message, process.env.REACT_APP_PAWN_ADMIN_FRONTEND_ENDPOINT!.toString());
        return;
      }

      if (data.jobId) {
        MySwal.fire({
          width: 1400,
          showCloseButton: true,
          title: '문의 작업',
          html: (
            <>
              <SendRedmine {...data} solType={solType.toUpperCase()} />
            </>
          ),
          showConfirmButton: false,
          customClass: 'swal2-modal-custom',
        });
      }
    } catch (error: any) {
      MySwal.fire({
        width: 1400,
        showCloseButton: true,
        title: '모달 생성 중 오류',
      });
    }
  }

  /**
   * @alias 어드민 - 상세 조회 - 리스트 엑셀 다운로드 뮤테이션
   */
  const { mutate: jobDetailSearchExcelMutate, isLoading: isExcelDownloading } = useMutation(jobDetailSearchExcelMutation, {
    onMutate: () => {
      // 로딩 표시
      Swal.fire({
        title: '잠시만 기다려 주세요.',
        html: '엑셀 다운로드 중 ...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },
    onSuccess: (blob) => {
      Swal.close();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'job-search-result.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // 다운로드 완료 메시지
      Swal.fire({
        title: '다운로드 완료',
        text: '엑셀 파일이 다운로드되었습니다.',
        icon: 'success',
        confirmButtonText: '확인',
      });
    },
    onError: (error: any) => {
      Swal.close();
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    },
  });

  /**
   * @alias 날짜 차이 계산
   * @example 28일 ~ 28일 = 0일
   */
  function calculateDateDifference(startDate: Date, endDate: Date): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    // 시분초 제거
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  }

  /**
   * @alias 어드민 - 상세 조회 - 리스트 엑셀 다운로드
   * @description 상세 조회 리스트 엑셀 다운로드
   * 1. 날짜 차이 계산 (시간 정보 제거하고 날짜만 비교)
   * 2. 1개월 이상인 경우 경고창 표시 후 함수 종료
   * 3. 다운로드 사유 묻기
   * 4. 엑셀 다운로드 동의 창
   * 5. 엑셀 파일 암호화 비밀번호 받기
   */
  async function jobDetailSearchExcel(search?: IGetJobData) {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const dateDiff = calculateDateDifference(start, end);

      // 1개월 이상인 경우 경고창 표시 후 함수 종료
      if (dateDiff > 30) {
        await Swal.fire({
          title: '기간 초과',
          text: '1개월 이상의 데이터는 조회할 수 없습니다.',
          icon: 'warning',
          confirmButtonText: '확인',
        });
        return;
      }
      // 다운로드 사유 묻기
      const reason = await Swal.fire({
        title: '다운로드 사유를 입력해주세요.',
        input: 'text',
        confirmButtonText: '확인',
      });
      if (!reason.isConfirmed) {
        return;
      }
      if (reason.value.length < 1) {
        await Swal.fire({
          title: '실패',
          html: '다운로드 사유를 입력해주세요.',
          icon: 'error',
        });
        return;
      }

      // 엑셀 다운로드 동의 창
      const agreeSwal = await Swal.fire({
        title: '아래 날짜 기준으로 엑셀 다운로드 하시겠습니까?',
        html: `
        <div>
          <p>시작일: ${getFormatDate(startDate)}</p>
          <p>종료일: ${getFormatDate(endDate)}</p>
          <p>쇼핑몰: ${searchSolType}</p>
          ${search?.searchValue || searchValue ? `<p>검색 값: ${search?.searchValue || searchValue}</p>` : ''}
          <p>다운로드 사유: ${reason.value}</p>
        </div>
        `,
        showCancelButton: true,
        cancelButtonText: '취소',
        confirmButtonText: '확인',
      });

      // 동의하지 않았을 때
      if (!agreeSwal.isConfirmed) {
        return;
      }

      // 엑셀 파일 암호화 비밀번호 받기
      const password = await Swal.fire({
        title: '엑셀 파일 암호화 비밀번호를 입력해주세요.',
        input: 'password',
        confirmButtonText: '확인',
      });
      if (!password.isConfirmed) {
        return;
      }
      if (password.value.length < 1) {
        await Swal.fire({
          title: '실패',
          html: '엑셀 파일 암호화 비밀번호를 입력해주세요.',
          icon: 'error',
        });
        return;
      }

      // useMutation 호출
      jobDetailSearchExcelMutate({
        startDate: getFormatDate(startDate),
        endDate: getFormatDate(endDate),
        solType: searchSolType,
        searchType: search?.searchType || searchType,
        searchValue: search?.searchValue || searchValue,
        downloadReason: reason.value,
        password: password.value,
        isIntegrated,
      });
    } catch (error: any) {
      await Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    }
  }

  /**
   * @alias 어드민 - 상세조회 - 작업상세결과 엑셀 다운로드 뮤테이션
   */
  const { mutate: detailJobExcelMutate, isLoading: isDetailJobExcelDownloading } = useMutation(detailJobExcelMutation, {
    onMutate: () => {
      // 로딩 표시
      Swal.fire({
        title: '잠시만 기다려 주세요.',
        html: '엑셀 다운로드 중 ...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },

    onSuccess: (blob) => {
      Swal.close();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'detail-job-result.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // 다운로드 완료 메시지
      Swal.fire({
        title: '다운로드 완료',
        text: '엑셀 파일이 다운로드되었습니다.',
        icon: 'success',
        confirmButtonText: '확인',
      });
    },
    onError: async (error: any) => {
      await Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    },
  });

  /**
   * @alias 어드민 - 상세조회 - 작업상세결과 엑셀 다운로드
   */
  async function detailJobExcel(search?: IGetJobData) {
    if (searchSolType !== 'COUPANG') {
      await Swal.fire({
        title: '오류',
        text: '쿠팡외의 솔루션에 대해서는 작업상세결과 다운로드를 지원하지 않습니다.',
        icon: 'warning',
        confirmButtonText: '확인',
      });
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const dateDiff = calculateDateDifference(start, end);

    // 1개월 이상인 경우 경고창 표시 후 함수 종료
    if (dateDiff > 30) {
      await Swal.fire({
        title: '기간 초과',
        text: '1개월 이상의 데이터는 조회할 수 없습니다.',
        icon: 'warning',
        confirmButtonText: '확인',
      });
      return;
    }

    // 다운로드 사유 묻기
    const reason = await Swal.fire({
      title: '다운로드 사유를 입력해주세요.',
      input: 'text',
      confirmButtonText: '확인',
    });
    if (!reason.isConfirmed) {
      return;
    }
    if (reason.value.length < 1) {
      await Swal.fire({
        title: '실패',
        html: '다운로드 사유를 입력해주세요.',
        icon: 'error',
      });
      return;
    }

    // 엑셀 다운로드 동의 창
    const agreeSwal = await Swal.fire({
      title: '아래 날짜 기준으로 엑셀 다운로드 하시겠습니까?',
      html: `
        <div>
          <p>시작일: ${getFormatDate(startDate)}</p>
          <p>종료일: ${getFormatDate(endDate)}</p>
          <p>쇼핑몰: ${searchSolType}</p>
          <p>다운로드 사유: ${reason.value}</p>
        </div>
        `,
      showCancelButton: true,
      cancelButtonText: '취소',
      confirmButtonText: '확인',
    });

    // 동의하지 않았을 때
    if (!agreeSwal.isConfirmed) {
      return;
    }
    // 엑셀 파일 암호화 비밀번호 받기
    const password = await Swal.fire({
      title: '엑셀 파일 암호화 비밀번호를 입력해주세요.',
      input: 'password',
      confirmButtonText: '확인',
    });
    if (!password.isConfirmed) {
      return;
    }
    if (password.value.length < 1) {
      await Swal.fire({
        title: '실패',
        html: '엑셀 파일 암호화 비밀번호를 입력해주세요.',
        icon: 'error',
      });
      return;
    }

    detailJobExcelMutate({
      startDate: getFormatDate(startDate),
      endDate: getFormatDate(endDate),
      solType: searchSolType,
      searchType: search?.searchType || searchType,
      searchValue: search?.searchValue || searchValue,
      downloadReason: reason.value,
      password: password.value,
      isIntegrated,
    });
  }

  return (
    <Container noBody={true}>
      {(solType === 'COUPANG' || isIntegrated) && (
        <Grid top={40}>
          ※ 쿠팡의 성공/실패 집계의 경우 쿠팡에 데이터를 전달하기까지의 집계이며, <br />
          실제 쿠팡 Wing 상품등록 성공 / 실패 여부 및 사유 는 돋보기/엑셀 다운로드를 통해 자세히 확인하실 수 있습니다.
        </Grid>
      )}
      <Grid display="flex" alignItems="center" top={40} bottom={20}>
        <Grid display="inline-flex" justifyContent="flex-start" alignItems="center">
          <DatePicker
            locale="ko"
            selected={startDate}
            popperPlacement="top-start"
            onChange={(targetDate: Date) => {
              setStartDate(targetDate);
            }}
            dateFormat="yyyy-MM-dd"
            customInput={<TextField variant="outlined" width={100} style={{ fontSize: '0.88rem' }} />}
          />
          &nbsp;~&nbsp;
          <DatePicker
            locale="ko"
            selected={endDate}
            popperPlacement="top-start"
            onChange={(targetDate: Date) => {
              setEndDate(targetDate);
            }}
            dateFormat="yyyy-MM-dd"
            customInput={<TextField variant="outlined" width={100} style={{ fontSize: '0.88rem' }} />}
          />
        </Grid>
        {isIntegrated && (
          <Grid display="flex" alignItems="center">
            <StyledExcelButton
              color="white"
              size="small"
              variant="outlined"
              disabled={isExcelDownloading}
              onClick={async () => {
                const result = await Swal.fire({
                  title: '다운로드',
                  text: '다운로드 할 내역을 선택해주세요',
                  showDenyButton: true,
                  showCancelButton: true,
                  confirmButtonText: '리스트',
                  denyButtonText: '상세내역',
                  cancelButtonText: '취소',
                  confirmButtonColor: '#3085d6',
                  denyButtonColor: '#5dade2',
                  cancelButtonColor: '#7a7a7a',
                });
                if (result.isConfirmed) {
                  // 리스트용 API
                  await jobDetailSearchExcel();
                } else if (result.isDenied) {
                  // 상세내역용 API
                  await detailJobExcel();
                } else {
                }
              }}
            >
              <RiFileExcel2Line size="1.2rem" />
              {isExcelDownloading ? '다운로드 중...' : '다운로드'}
            </StyledExcelButton>
          </Grid>
        )}
        {/* <Select variant="outlined" {...useForm.register(`wakeUpType`)}>
            {WAKEUP.map((one, index) => {
              if (one === 'WEMAKEPRICE')
                return (
                  <option value={one} key={index}>
                    {one}
                  </option>
                );
            })}
          </Select> */}
        <Grid display="flex" justifyContent="flex-end">
          {/* 솔루션 타입 */}
          {isIntegrated ? (
            <Select
              value={searchSolType}
              onChange={(event) => setSearchSolType(event.target.value as WakeUp)}
              variant="outlined"
              style={{ fontSize: '0.88rem' }}
            >
              <option value="COUPANG">쿠팡</option>
              {/* <option value="WEMAKEPRICE">위메프</option> */}
              <option value="ST11">11번가</option>
              <option value="ALWAYZ">올웨이즈</option>
              <option value="KAKAO">카카오</option>
              <option value="ESM">ESM+</option>
              <option value="TOSS">TOSS</option>
              {/* <option value="ALL">ALL</option> */}
            </Select>
          ) : (
            <></>
          )}
          {/* 조회 키 */}
          <Select value={searchType} onChange={(event) => setSearchType(event.target.value)} variant="outlined" style={{ fontSize: '0.88rem' }}>
            <option value="playautoId">플레이오토 ID</option>
            {/* <option value="marketId">가져오는 쇼핑몰 ID</option> */}
            <option value="jobId">작업번호</option>
            {searchSolType === 'COUPANG' ? (
              <option value="coupangVendorId">쿠팡 업체코드</option>
            ) : searchSolType === 'ESM' ? (
              <>
                <option value="gmarketId">{WAKEUP_FORM_DATA.GMARKET.name.type1} 아이디</option>
                <option value="auctionId">{WAKEUP_FORM_DATA.AUCTION.name.type1} 아이디</option>
              </>
            ) : (
              <option value="wakeupId">{WAKEUP_FORM_DATA[searchSolType].name.type1} 아이디</option>
            )}
          </Select>
          &nbsp;
          {/* 조회 값 */}
          <TextField
            type="text"
            placeholder="조회 할 내용"
            variant="outlined"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            style={{ fontSize: '0.88rem' }}
          />
          &nbsp;
          <Button color="main" width={80} size="small" borderRadius={5} onClick={onSubmit}>
            검색
          </Button>
        </Grid>
      </Grid>
      <Grid>
        <Table
          headerItems={[
            {
              width: 160,
              headerName: '작업날짜',
              align: 'center',
              headerAlign: 'center',
              editable: true,
            },
            {
              width: 120,
              headerName: '플레이오토ID',
              align: 'center',
              headerAlign: 'center',
              editable: true,
            },
            {
              width: 100,
              headerName: `${WAKEUP_FORM_DATA.GMARKET.name.type1}아이디`,
              align: 'center',
              headerAlign: 'center',
              editable: true,
              hideable: solType === 'ESM' ? false : true,
            },
            {
              width: 100,
              headerName: `${WAKEUP_FORM_DATA.AUCTION.name.type1}아이디`,
              align: 'center',
              headerAlign: 'center',
              editable: true,
              hideable: solType === 'ESM' ? false : true,
            },
            {
              width: 100,
              headerName: `${WAKEUP_FORM_DATA[searchSolType].name.type1}아이디`,
              align: 'center',
              headerAlign: 'center',
              editable: true,
              // hideable: solType === 'WEMAKEPRICE' ? false : true,
              hideable: true,
            },
            {
              width: 100,
              headerName: '쿠팡업체코드',
              align: 'center',
              headerAlign: 'center',
              editable: true,
              hideable: solType === 'COUPANG' ? false : true,
            },
            {
              width: 120,
              headerName: '가져오는 쇼핑몰',
              align: 'center',
              headerAlign: 'center',
              editable: true,
            },
            {
              width: 120,
              headerName: '이전한 쇼핑몰',
              align: 'center',
              headerAlign: 'center',
              editable: true,
              hideable: ['ALL', 'ESM'].includes(solType) ? false : true,
            },
            {
              width: 100,
              headerName: '작업상태',
              align: 'center',
              headerAlign: 'center',
              editable: true,
            },
            {
              width: 140,
              headerName: '작업메세지',
              align: 'center',
              headerAlign: 'center',
              editable: true,
            },
            {
              width: 90,
              headerName: '성공/실패',
              align: 'center',
              headerAlign: 'center',
              editable: true,
            },
            {
              width: 110,
              headerName: '작업번호',
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                return (
                  <Grid>
                    <Text fontSize="0.77rem" fontWeight={500} lineHeight={24}>
                      {params.value.jobId}
                    </Text>
                    <Grid display="flex" justifyContent="center">
                      <ButtonTag
                        color="white"
                        size="small"
                        variant="outlined"
                        width={40}
                        borderRadius={5}
                        onClick={() => {
                          jobDetailEvent('VIEW', params.value.jobId, (params.value.successCount || 0) + (params.value.failureCount || 0));
                        }}
                      >
                        <BiSearch size="0.77rem" />
                      </ButtonTag>
                      <ButtonTag
                        color="white"
                        size="small"
                        variant="outlined"
                        width={40}
                        borderRadius={5}
                        onClick={() => {
                          jobDetailEvent('EXCEL', params.value.jobId, (params.value.successCount || 0) + (params.value.failureCount || 0));
                        }}
                      >
                        <RiFileExcel2Line size="0.77rem" />
                      </ButtonTag>

                      {/* {isIntegrated && (
                        <ButtonTag
                          color="white"
                          size="small"
                          variant="outlined"
                          width={40}
                          borderRadius={5}
                          onClick={() => {
                            jobForceFailure(params.value.jobId);
                          }}
                        >
                          <Tooltip title="작업을 강제 실패처리합니다.">
                            <RiAlarmWarningFill size="0.77rem" />
                          </Tooltip>
                        </ButtonTag>
                      )} */}
                    </Grid>
                  </Grid>
                );
              },
            },
            {
              width: 100,
              headerName: '지정상품',
              align: 'center',
              headerAlign: 'center',
              editable: true,
            },
            {
              width: 80,
              headerName: '작업실패',
              align: 'center',
              headerAlign: 'center',
              editable: true,
              hideable: !isIntegrated,
              renderCell: (params: any) => {
                const jobId = params.row.col7.jobId;
                const createdAt = params.row.col0;
                return (
                  <ButtonTag
                    color="white"
                    size="small"
                    variant="outlined"
                    width={40}
                    borderRadius={5}
                    onClick={() => {
                      jobForceFailure(jobId, createdAt);
                    }}
                  >
                    <RiAlarmWarningFill size="0.77rem" />
                  </ButtonTag>
                );
              },
            },
            {
              width: 80,
              headerName: '레드마인',
              align: 'center',
              headerAlign: 'center',
              hideable: !isIntegrated,
              renderCell: (params: any) => {
                const email = params.row.col1.toString();
                const scrapShopName = params.row.col3.toString();
                const state = params.row.col4;
                const failureMessage = params.row.col5;
                const jobId = params.row.col7.jobId.toString();
                const count = (params.row.col7.successCount || 0) + (params.row.col7.failureCount || 0);
                const successCount = params.row.col7.successCount;
                const failureCount = params.row.col7.failureCount;
                const targetShopName = searchSolType.toString();
                return (
                  <Grid display="flex" justifyContent="center">
                    <ButtonTag
                      color="white"
                      size="small"
                      variant="outlined"
                      width={40}
                      borderRadius={5}
                      onClick={() => {
                        createJobInquiry({ jobId, count, email, scrapShopName, targetShopName, failureMessage, state, successCount, failureCount });
                      }}
                    >
                      <SiRedmine size="0.77rem" />
                    </ButtonTag>
                  </Grid>
                );
              },
            },
          ]}
          bodyItems={jobData.map((one) => [
            one.startDate,
            one.email,
            one.wakeupEtc2,
            one.wakeupEtc1,
            one.wakeupId,
            one.wakeupEtc5,
            one.scrapShopName,
            one.targetShopName,
            one.state,
            one.msg,
            `${one.successCount || 0} / ${one.failureCount || 0}`,
            {
              jobId: one.jobId,
              successCount: one.successCount,
              failureCount: one.failureCount,
            },
            one.scrapType,
            // <>
            //   <Text fontSize="0.9rem" fontWeight={500}>
            //     {one.job_id}
            //   </Text>
            //   <Grid display="flex" justifyContent="center">
            //     <ButtonTag
            //       color="white"
            //       size="small"
            //       variant="outlined"
            //       width={40}
            //       borderRadius={5}
            //       onClick={() => {
            //         jobDetailEvent('VIEW', one.job_id, (one.successCount || 0) + (one.failureCount || 0));
            //       }}
            //     >
            //       <BiSearch />
            //     </ButtonTag>
            //     <ButtonTag
            //       color="white"
            //       size="small"
            //       variant="outlined"
            //       width={40}
            //       borderRadius={5}
            //       onClick={() => {
            //         jobDetailEvent('EXCEL', one.job_id, (one.successCount || 0) + (one.failureCount || 0));
            //       }}
            //     >
            //       <RiFileExcel2Line />
            //     </ButtonTag>
            //   </Grid>
            // </>,
          ])}
        />
        <Grid top={30} />
        <Pagination nowPage={nowPage} max={max} size={5} nowPageEvent={setNowPage} />
      </Grid>
    </Container>
  );
}
